import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { legalApi, LegalStatus } from '@/features/security/api/legal-api';

/**
 * #1060 — interruptor del pedido de aceptación, APAGADO por defecto.
 *
 * Compara contra `'true'` y no usa `Boolean(...)`: en Vite las variables de
 * entorno llegan como STRING, y `Boolean('false')` es `true`. Una comparación
 * laxa acá encendería el modal para 94 clientes con la variable puesta en
 * `false` — el error se vería exactamente igual que un encendido intencional.
 *
 * Se lee dentro de una función y NO en una constante de módulo. La primera
 * versión la fijaba al importar, y eso hacía que el valor quedara congelado
 * antes de que un test pudiera cambiarlo: los specs del comportamiento medían
 * el flag en vez de lo que decían medir, y hacía falta reimportar el módulo
 * para tocarlo. En producción el resultado es el mismo —Vite inyecta el literal
 * igual— y acá se vuelve verificable.
 */
function pedidoActivo(): boolean {
  return import.meta.env.VITE_LEGAL_CONSENT_ENABLED === 'true';
}

export interface UseLegalConsentReturn {
  /** true = hay que frenar y pedir la aceptación antes de dejar entrar. */
  debePedir: boolean;
  estado: LegalStatus | null;
  enviando: boolean;
  error: string | null;
  aceptar: () => Promise<void>;
}

/**
 * #1060 — decide si este usuario tiene que aceptar antes de seguir.
 *
 * DOS DECISIONES QUE NO SON DE ESTILO, y por las que existe este comentario:
 *
 * 1. **La fuente de verdad es el server, nunca localStorage.**
 *    El modal de trial que vive al lado usa una bandera local para no repetirse
 *    (`trial-activated-modal-shown`), y para ese caso está bien. Acá sería el
 *    bug otra vez: una marca local diría "ya aceptó" mientras la base sigue con
 *    `termsAcceptedAt` en NULL, y la Ley 172-13 no pide que el usuario haya
 *    aceptado — pide poder PROBARLO. Un localStorage no prueba nada ante un
 *    reclamo. Por eso `debePedir` sale de `pending` del backend, siempre.
 *
 * 2. **Un fallo de RED no bloquea el dashboard.**
 *    Si la consulta no llega (timeout, 500, offline), NO pedimos aceptación:
 *    `debePedir` queda false. Es deliberado y es el lado barato del error.
 *    Al revés —fallar cerrado ante cualquier tropiezo— un parpadeo de red
 *    dejaría a las seis agencias sin poder entrar a su propio panel, y el
 *    remedio sería peor que lo que remedia. Fallar abierto acá cuesta que a
 *    ese usuario se le pregunte en la próxima carga, y mientras tanto el
 *    conteo en la base sigue mostrando el hueco: el error se NOTA y es
 *    recuperable. Un 401 tampoco pide nada: si no hay sesión, no hay a quién
 *    preguntarle.
 */
export const useLegalConsent = (): UseLegalConsentReturn => {
  const { isAuthenticated, user } = useAuth();
  const [estado, setEstado] = useState<LegalStatus | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El usuario puede cambiar (impersonación, re-login) y hay que volver a
  // preguntar por el nuevo. Guardamos por quién preguntamos, no un booleano.
  const consultadoPara = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id ?? null;

    if (!isAuthenticated || !userId) {
      consultadoPara.current = null;
      setEstado(null);
      return;
    }
    if (consultadoPara.current === userId) return;
    consultadoPara.current = userId;

    let vigente = true;
    legalApi
      .getStatus()
      .then((r) => {
        if (vigente) setEstado(r);
      })
      .catch(() => {
        // Silencio deliberado — ver punto 2 del comentario de arriba. No
        // guardamos estado: sin estado, `debePedir` es false y el usuario pasa.
        if (vigente) setEstado(null);
      });

    return () => {
      vigente = false;
    };
  }, [isAuthenticated, user?.id]);

  const aceptar = useCallback(async () => {
    setEnviando(true);
    setError(null);
    try {
      const nuevo = await legalApi.accept();
      setEstado(nuevo);
    } catch (e: any) {
      // Acá SÍ fallamos cerrado: el modal no se cierra. Cerrarlo tras un error
      // dejaría al usuario adentro creyendo que aceptó, con la base vacía —
      // que es exactamente el estado que produjo este issue.
      setError(
        e?.response?.data?.message ??
          'No pudimos registrar tu aceptación. Reintenta en un momento.'
      );
    } finally {
      setEnviando(false);
    }
  }, []);

  return {
    // #1060/#1074 — el pedido está APAGADO por defecto, y esto no es timidez.
    //
    // Producción va 6 commits atrás y creciendo: el freno de deploy está
    // funcionando como control de seguridad sin que nadie lo haya diseñado así
    // (lo nombró RDIDB el 17-ago). Cuando se libere, sale TODO junto — y sin
    // este interruptor, "desplegar" y "pedirle a 94 clientes que acepten" son
    // el mismo acto, decidido por quien mergee primero.
    //
    // Con el flag son dos actos separados: el código puede viajar cuando la
    // cola se libere, y el pedido se enciende cuando Randhy lo diga, que es
    // de quien es esa decisión. Un despliegue no debería tomar por él una
    // decisión que sale hacia clientes.
    //
    // CÓMO SE ENCIENDE Y SE APAGA — corregido 2026-08-17 (TINA-RDICTO).
    //
    // Este comentario decía: «Encender: VITE_LEGAL_CONSENT_ENABLED=true. Apagar
    // es quitar la variable, sin revertir código ni volver a desplegar el bundle
    // entero.» **Las dos mitades eran falsas**, y lo dice el propio
    // `Dockerfile.railway:43-66`, que se escribió después:
    //
    //   · Vite inlina las VITE_* en BUILD-TIME. Ponerla en el env del servicio
    //     de Railway NO llega al `RUN pnpm run build`. Se enciende cambiando el
    //     default del Dockerfile a `true`, con commit y deploy.
    //   · Apagar NO es quitar la variable: el valor ya está horneado en el
    //     bundle. Hay que volver el default a `false` y **reconstruir**.
    //
    // Por qué importa que esto esté bien escrito: es el ROLLBACK de un cambio
    // que sale hacia 94 clientes. Si se enciende y hay que apagarlo rápido,
    // alguien va a intentar «quitar la variable» y el modal va a seguir ahí.
    // Un interruptor que se documenta como runtime y es build-time convierte un
    // rollback de un minuto en uno de un deploy.
    //
    // Encenderlo es decisión de Randhy.
    debePedir: pedidoActivo() && Boolean(estado?.pending),
    estado,
    enviando,
    error,
    aceptar,
  };
};
